import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelSelector } from '@/components/composer/ChannelSelector';
import { cn } from '@/utils/cn';
import type { PublishMode } from '../ModeSelector';
import type { MediaType } from '@/constants/platformMediaRequirements';
import type { PlatformOptions } from '@/types/scheduled-instances';
import type { MediaItem } from '@/types/posts';

/**
 * PlatformsSection Component
 *
 * Channel selection with avatar grid matching old system design
 * - Circular avatars grouped by platform
 * - Platform badge overlays on each avatar
 * - Visual selection indicators (ring around selected)
 * - Optional in draft mode, required in shoot_now/schedule modes
 */

interface PlatformsSectionProps {
  selectedChannels: number[];
  onSelectionChange: (channelIds: number[]) => void;
  disabled: boolean;
  mode: PublishMode;
  /** Current media types in the post for compatibility filtering */
  mediaTypes?: MediaType[];
  /** Channel platform options for inline display under clusters */
  channelOptions?: Record<number, PlatformOptions>;
  onOptionsChange?: (channelId: number, options: PlatformOptions) => void;
  hasVideo?: boolean;
  hasMedia?: boolean;
  /** Caption for inline platform preview */
  caption?: string;
  /** Media for inline platform preview */
  media?: MediaItem[];
  isCarouselMode?: boolean;
  channels?: Array<{
    platform: string;
    displayName: string;
    handle: string;
    avatarUrl?: string;
    channelId: number;
    options?: PlatformOptions;
  }>;
}

export const PlatformsSection = memo(({
  selectedChannels,
  onSelectionChange,
  disabled,
  mode,
  mediaTypes,
  channelOptions,
  onOptionsChange,
  hasVideo,
  hasMedia,
  caption,
  media,
  isCarouselMode,
  channels,
}: PlatformsSectionProps) => {
  const { t } = useTranslation('common');
  const isDraftMode = mode === 'draft';
  const showValidationWarning = !isDraftMode && selectedChannels.length === 0;

  return (
    <div className={cn(isDraftMode && 'opacity-75')}>
      {isDraftMode && (
        <div className="flex items-center gap-2 mb-3">
          <label className="block text-sm font-medium text-bs-dark">
            {t('platforms')}
          </label>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
            {t('optional_for_drafts')}
          </span>
        </div>
      )}
      <ChannelSelector
        selectedChannels={selectedChannels}
        onSelectionChange={onSelectionChange}
        disabled={disabled}
        mediaTypes={mediaTypes}
        channelOptions={channelOptions}
        onOptionsChange={onOptionsChange}
        hasVideo={hasVideo}
        hasMedia={hasMedia}
        caption={caption}
        media={media}
        isCarouselMode={isCarouselMode}
        channels={channels}
      />
      {showValidationWarning && (
        <p className="mt-3 text-xs text-amber-600">
          {t('select_platform_to_publish')}
        </p>
      )}
      {isDraftMode && selectedChannels.length === 0 && (
        <p className="mt-3 text-xs text-bs-gray">
          {t('select_channels_preview')}
        </p>
      )}
    </div>
  );
});
PlatformsSection.displayName = 'PlatformsSection';
